import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import Galaxy from "../animations/Galaxy";
import PixelCard from "../animations/PixelCard";
import QuoteModal from "../QuoteModal/QuoteModal";

// Image imports (using your provided path)
import heroIcon from "../../images/icon/animated-gif02.gif";
import featureIcon1 from "../../images/icon/feature-icon01.svg";
import featureIcon2 from "../../images/icon/feature-icon02.svg";
import featureIcon3 from "../../images/icon/feature-icon03.svg";
import mobileBg from "../../images/hero/mobile-bg.png";

const ANIMATED_WORDS = ["Future", "Evolution", "Unknown", "Continuity"];

const HeroSection: React.FC = () => {
  const wordRef = useRef<HTMLSpanElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const currentIndex = useRef(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer to track hero visibility
  useEffect(() => {
    const heroElement = heroRef.current;
    if (!heroElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Add a small buffer - unmount when less than 10% visible
          setIsVisible(entry.isIntersecting || entry.intersectionRatio > 0.1);
        });
      },
      {
        threshold: [0, 0.1, 0.25],
        rootMargin: "100px 0px 0px 0px" // Keep rendering a bit before fully out of view
      }
    );

    observer.observe(heroElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!wordRef.current) return;

    const typeSpeed = 80; // ms per character for typing
    const deleteSpeed = 50; // ms per character for deleting
    const pauseAfterType = 2000; // pause after typing complete
    const pauseAfterDelete = 300; // pause after deleting complete

    let timeoutId: ReturnType<typeof setTimeout>;
    let isAnimating = true;

    const typeWord = (word: string, charIndex: number, onComplete: () => void) => {
      if (!isAnimating || !wordRef.current) return;

      if (charIndex <= word.length) {
        wordRef.current.textContent = word.substring(0, charIndex);
        timeoutId = setTimeout(() => {
          typeWord(word, charIndex + 1, onComplete);
        }, typeSpeed);
      } else {
        onComplete();
      }
    };

    const deleteWord = (word: string, charIndex: number, onComplete: () => void) => {
      if (!isAnimating || !wordRef.current) return;

      if (charIndex >= 0) {
        wordRef.current.textContent = word.substring(0, charIndex);
        timeoutId = setTimeout(() => {
          deleteWord(word, charIndex - 1, onComplete);
        }, deleteSpeed);
      } else {
        onComplete();
      }
    };

    const animateCycle = () => {
      if (!isAnimating || !wordRef.current) return;

      const currentWord = ANIMATED_WORDS[currentIndex.current];

      // Delete current word
      deleteWord(currentWord, currentWord.length, () => {
        timeoutId = setTimeout(() => {
          // Move to next word
          currentIndex.current = (currentIndex.current + 1) % ANIMATED_WORDS.length;
          const nextWord = ANIMATED_WORDS[currentIndex.current];

          // Type next word
          typeWord(nextWord, 0, () => {
            timeoutId = setTimeout(() => {
              animateCycle();
            }, pauseAfterType);
          });
        }, pauseAfterDelete);
      });
    };

    // Initial typing animation for first word
    const firstWord = ANIMATED_WORDS[0];
    timeoutId = setTimeout(() => {
      typeWord(firstWord, 0, () => {
        timeoutId = setTimeout(() => {
          animateCycle();
        }, pauseAfterType);
      });
    }, 500);

    return () => {
      isAnimating = false;
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <section className="hero hero-style pos-rel bg_img" ref={heroRef}>
      <div className="pos-absolute" style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, width: '100%', height: '100%' }}>
        {/* Show Galaxy on desktop, static bg on mobile */}
        {isMobile ? (
          <div 
            style={{ 
              width: '100%', 
              height: '100%', 
              backgroundImage: `url(${mobileBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }} 
          />
        ) : (
          isVisible && (
            <Galaxy 
              mouseRepulsion={true}
              mouseInteraction={true}
              density={1.9}
              glowIntensity={0.2}
              saturation={0.1}
              hueShift={240}
              starSpeed={0.07}
            />
          )
        )}
      </div>
      <div className="container pos-rel" style={{ zIndex: 1 }}>
        <div className="row">
          {/* ---------- Left Content ---------- */}
          <div className="col-lg-9 col-md-9">
            <div className="hero-content">
              <h2 className="title scale-animation wow">
                Engineering the Intelligent{" "}
                <span
                  ref={wordRef}
                  style={{
                    display: "inline-block",
                    position: "relative",
                    willChange: "transform, opacity, filter, clip-path",
                    minWidth: "120px",
                    textAlign: "left",
                  }}
                >
                  {ANIMATED_WORDS[0]}
                </span>
              </h2>
              <p className="sub-title scale-animation wow">
                Eliminate bottlenecks with precision-engineered AI and high-velocity automation. Transform operational complexity into your ultimate competitive edge.
              </p>
              <div className="hero-btn scale-animation wow">
                {/* <Link className="thm-btn agency-btn" to="/contact">
                  <span className="text">Begin Today with us</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5.06592"
                          y="19.9785"
                          width="20.5712"
                          height="2.61221"
                          transform="rotate(-40.2798 5.06592 19.9785)"
                          fill="white"
                        />
                        <rect
                          x="7.97095"
                          y="7.24463"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 7.97095 7.24463)"
                          fill="white"
                        />
                        <rect
                          x="11.6523"
                          y="7.54834"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 11.6523 7.54834)"
                          fill="white"
                        />
                        <rect
                          x="15.334"
                          y="7.85205"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 15.334 7.85205)"
                          fill="white"
                        />
                        <rect
                          x="18.7119"
                          y="11.8374"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.7119 11.8374)"
                          fill="white"
                        />
                        <rect
                          x="18.4084"
                          y="15.52"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.4084 15.52)"
                          fill="white"
                        />
                        <rect
                          x="18.104"
                          y="19.2012"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.104 19.2012)"
                          fill="white"
                        />
                      </svg>
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5.06592"
                          y="19.9785"
                          width="20.5712"
                          height="2.61221"
                          transform="rotate(-40.2798 5.06592 19.9785)"
                          fill="white"
                        />
                        <rect
                          x="7.97095"
                          y="7.24463"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 7.97095 7.24463)"
                          fill="white"
                        />
                        <rect
                          x="11.6523"
                          y="7.54834"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 11.6523 7.54834)"
                          fill="white"
                        />
                        <rect
                          x="15.334"
                          y="7.85205"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 15.334 7.85205)"
                          fill="white"
                        />
                        <rect
                          x="18.7119"
                          y="11.8374"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.7119 11.8374)"
                          fill="white"
                        />
                        <rect
                          x="18.4084"
                          y="15.52"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.4084 15.52)"
                          fill="white"
                        />
                        <rect
                          x="18.104"
                          y="19.2012"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.104 19.2012)"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </span>
                </Link> */}
                <button 
                  className="thm-btn agency-btn quote-btn" 
                  onClick={() => setIsQuoteModalOpen(true)}
                >
                  <span className="text">Begin Today with us</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5.06592"
                          y="19.9785"
                          width="20.5712"
                          height="2.61221"
                          transform="rotate(-40.2798 5.06592 19.9785)"
                          fill="white"
                        />
                        <rect
                          x="7.97095"
                          y="7.24463"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 7.97095 7.24463)"
                          fill="white"
                        />
                        <rect
                          x="11.6523"
                          y="7.54834"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 11.6523 7.54834)"
                          fill="white"
                        />
                        <rect
                          x="15.334"
                          y="7.85205"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 15.334 7.85205)"
                          fill="white"
                        />
                        <rect
                          x="18.7119"
                          y="11.8374"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.7119 11.8374)"
                          fill="white"
                        />
                        <rect
                          x="18.4084"
                          y="15.52"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.4084 15.52)"
                          fill="white"
                        />
                        <rect
                          x="18.104"
                          y="19.2012"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.104 19.2012)"
                          fill="white"
                        />
                      </svg>
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5.06592"
                          y="19.9785"
                          width="20.5712"
                          height="2.61221"
                          transform="rotate(-40.2798 5.06592 19.9785)"
                          fill="white"
                        />
                        <rect
                          x="7.97095"
                          y="7.24463"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 7.97095 7.24463)"
                          fill="white"
                        />
                        <rect
                          x="11.6523"
                          y="7.54834"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 11.6523 7.54834)"
                          fill="white"
                        />
                        <rect
                          x="15.334"
                          y="7.85205"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 15.334 7.85205)"
                          fill="white"
                        />
                        <rect
                          x="18.7119"
                          y="11.8374"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.7119 11.8374)"
                          fill="white"
                        />
                        <rect
                          x="18.4084"
                          y="15.52"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.4084 15.52)"
                          fill="white"
                        />
                        <rect
                          x="18.104"
                          y="19.2012"
                          width="2.61221"
                          height="2.61221"
                          transform="rotate(-40.2798 18.104 19.2012)"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* ---------- Right Icon ---------- */}
          <div className="col-lg-3 col-md-3">
            <div
              className="hero-icon wow zoomIn"
              data-wow-delay="700ms"
              data-wow-duration="800ms"
            >
              <img src={heroIcon} alt="Hero icon" />
            </div>
          </div>
        </div>

        {/* ---------- Features ---------- */}
        <div className="row mt-55">
          <div className="col-lg-4 col-md-6 mt-30">
            <div
              className="xb-feature-item wow fadeInUp"
              data-wow-delay="700ms"
              data-wow-duration="600ms"
            >
              <div className="xb-item--inner xb-border">
                <span className="xb-item--icon">
                  <img src={featureIcon1} alt="icon" />
                </span>
                <div className="xb-item--holder">
                  <h2 className="xb-item--title">Predictive Intelligence</h2>
                  <p className="xb-item--content">
                    Accelerate your decision-making with real-time AI analytics that transform raw data into actionable foresight.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 mt-30">
            <div
              className="xb-feature-item wow fadeInUp"
              data-wow-delay="800ms"
              data-wow-duration="600ms"
            >
              <div className="xb-item--inner xb-border">
                <span className="xb-item--icon">
                  <img src={featureIcon2} alt="icon" />
                </span>
                <div className="xb-item--holder">
                  <h2 className="xb-item--title">Seamless AI integration</h2>
                  <p className="xb-item--content">
                    Zero friction, zero add-ons. Deploy sophisticated, embedded AI capabilities that scale alongside your vision.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 mt-30">
            <div
              className="xb-feature-item wow fadeInUp"
              data-wow-delay="900ms"
              data-wow-duration="600ms"
            >
              <div className="xb-item--inner xb-border">
                <span className="xb-item--icon">
                  <img src={featureIcon3} alt="icon" />
                </span>
                <div className="xb-item--holder">
                  <h2 className="xb-item--title">End-to-end automation</h2>
                  <p className="xb-item--content">
                    Streamline every touchpoint with smart automation that identifies and solves delays in real-time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;
